import React, { useEffect, useMemo, useState } from "react";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import { Handshake, Check, MessageSquare } from "lucide-react";
import { CommitmentBadge, RelationBadge } from "@/components/Badges";
import { formatDate } from "@/lib/utils-fos";
import { ReachOutModal } from "@/pages/Inbox";

// -----------------------------------------------------------------------------
// Commitments — dedicated top-level page.
// Real 4-column kanban board keyed on the backend Commitment.status values
// (open, at_risk, overdue, done). On mobile the columns stack to 1-col.
// -----------------------------------------------------------------------------

// Column config matches backend Commitment.status: "open, done, overdue, at_risk"
const COLUMNS = [
  { key: "overdue", label: "Overdue",   tone: "rose",    dot: "#EF4444" },
  { key: "at_risk", label: "At risk",   tone: "amber",   dot: "#F59E0B" },
  { key: "open",    label: "On track",  tone: "emerald", dot: "#10B981" },
  { key: "done",    label: "Closed",    tone: "neutral", dot: "#A3A3A3" },
];

export default function Commitments() {
  const [commitments, setCommitments] = useState([]);
  const [people, setPeople] = useState([]);
  const [projects, setProjects] = useState([]);
  const [followUp, setFollowUp] = useState(null);

  const load = async () => {
    const [cc, pp, pr] = await Promise.all([
      endpoints.commitments(),
      endpoints.people(),
      endpoints.projects(),
    ]);
    setCommitments(cc);
    setPeople(pp);
    setProjects(pr);
  };
  useEffect(() => { load(); }, []);

  const peopleById = useMemo(() => Object.fromEntries(people.map((p) => [p.id, p])), [people]);
  const projectsById = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);

  // Bucket every commitment by status (any unknown value falls into "open").
  const groups = useMemo(() => {
    const validKeys = new Set(COLUMNS.map((c) => c.key));
    const map = Object.fromEntries(COLUMNS.map((c) => [c.key, []]));
    for (const c of commitments) {
      const k = validKeys.has(c.status) ? c.status : "open";
      map[k].push(c);
    }
    // Sort each bucket by due_date ascending (earliest first).
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
    }
    return map;
  }, [commitments]);

  const markDone = async (c) => {
    await endpoints.updateCommitment(c.id, { status: "done", resolved_at: new Date().toISOString() });
    setCommitments((prev) => prev.map((x) => (x.id === c.id ? { ...x, status: "done" } : x)));
    toast.success("Commitment closed");
  };

  const openFollowUp = (c) => {
    const target = peopleById[c.from_person_id];
    const project = projectsById[c.project_id];
    const platform = (target?.preferred_channel || target?.communication_preference || "email").toLowerCase();
    setFollowUp({
      platform: ["email", "slack", "whatsapp", "linkedin", "twitter", "sms"].includes(platform) ? platform : "email",
      to_person_id: target?.id || "",
      to_display: target?.email || "",
      subject: `Following up: ${c.what}`,
      body: (
        `Hi ${target?.name?.split(" ")[0] || "there"},\n\n` +
        `Circling back on: ${c.what}` +
        (project ? ` (${project.name})` : "") + `.\n\n` +
        `Was due ${formatDate(c.due_date)}. Anything I can do to unblock?`
      ),
      related_project_id: c.project_id || null,
    });
  };

  const openCount = commitments.filter((c) => c.status !== "done").length;

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="commitments-page">
      <header>
        <h1 className="text-2xl font-semibold text-neutral-900 flex items-center gap-2">
          <Handshake size={22} className="text-neutral-700" /> Commitments
        </h1>
        <p className="text-sm text-neutral-500 mt-1">
          {openCount} open across {projects.length} project{projects.length === 1 ? "" : "s"}. Earliest due first.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {COLUMNS.map((col) => (
          <KanbanColumn
            key={col.key}
            col={col}
            items={groups[col.key]}
            peopleById={peopleById}
            projectsById={projectsById}
            onDone={markDone}
            onFollowUp={openFollowUp}
          />
        ))}
      </div>

      {followUp && (
        <ReachOutModal
          initial={followUp}
          people={people}
          onClose={() => setFollowUp(null)}
          onSent={() => { setFollowUp(null); load(); }}
        />
      )}
    </div>
  );
}

function KanbanColumn({ col, items, peopleById, projectsById, onDone, onFollowUp }) {
  const closed = col.key === "done";
  return (
    <section
      className="fos-card p-3 flex flex-col min-h-[280px]"
      data-testid={`commitments-col-${col.key}`}
    >
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: col.dot }} />
          <h2 className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">{col.label}</h2>
        </div>
        <span className="text-[11px] mono text-neutral-500">{items.length}</span>
      </div>

      <div className="space-y-2 flex-1">
        {items.length === 0 && (
          <p className="text-[11px] italic text-neutral-400 px-1 py-6 text-center">Nothing here.</p>
        )}

        {items.map((c) => (
          <CommitmentCard
            key={c.id}
            commitment={c}
            peopleById={peopleById}
            projectsById={projectsById}
            onDone={onDone}
            onFollowUp={onFollowUp}
            closed={closed}
          />
        ))}
      </div>
    </section>
  );
}

function CommitmentCard({ commitment: c, peopleById, projectsById, onDone, onFollowUp, closed }) {
  return (
    <div className="fos-card p-3 border border-neutral-200 bg-white" data-testid={`commitment-${c.id}`}>
      <p className="text-sm text-neutral-900 leading-snug mb-2">{c.what}</p>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-neutral-500 mb-2">
        {c.due_date && <span>Due {formatDate(c.due_date)}</span>}
        {peopleById[c.from_person_id] && (
          <span className="inline-flex items-center gap-1">
            From <RelationBadge person={peopleById[c.from_person_id]} />
          </span>
        )}
        {peopleById[c.to_person_id] && c.to_person_id !== c.from_person_id && (
          <span className="inline-flex items-center gap-1">
            To <RelationBadge person={peopleById[c.to_person_id]} />
          </span>
        )}
        <CommitmentBadge status={c.status} />
      </div>

      {projectsById[c.project_id] && (
        <p className="text-[10px] mono uppercase tracking-wide text-neutral-500 truncate mb-2">
          · {projectsById[c.project_id].name}
        </p>
      )}

      {!closed && (
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => onFollowUp(c)}
            data-testid={`commitment-followup-${c.id}`}
            className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] px-2 py-1.5 rounded-sm border border-neutral-200 hover:bg-neutral-50 text-blue-600 font-medium"
          >
            <MessageSquare size={11} /> Follow up
          </button>
          <button
            onClick={() => onDone(c)}
            data-testid={`commitment-done-${c.id}`}
            className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] px-2 py-1.5 rounded-sm border border-neutral-200 hover:bg-neutral-50 text-blue-600 font-medium"
          >
            <Check size={11} /> Done
          </button>
        </div>
      )}
    </div>
  );
}
